import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { store } from '../../config/firebase';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Scoreboard from '../ui/gameControls/scoreboard';
import ScoringDisplay from '../ui/playerDisplay/scoringDisplay';
import ViewScorecardPhoto from '../ui/gameControls/viewScorecardPhoto';
import GameByStatusCardPublicBasic from '../ui/gameStatusCards/gameByStatusCardPublicBasic';
import { GameStatus } from '../../constants/gameConstants';
import { signOutUser } from '../../services/auth';
import { useColorMode } from '../../hooks/colorModeProvider';

const PublicScoring = () => {
  const { gameId } = useParams();
  const [searchParams] = useSearchParams();
  const [fsGame, setFSGame] = React.useState({});
  const snapshotSeq = React.useRef(0);
  const dispatch = useDispatch();

  const { mode, setMode } = useColorMode();

  // BSA from the query string when coming from the my-games list; fall back
  // to the first member's BSA so standalone games always have a back link.
  const bsaNumber = searchParams.get('bsa') || fsGame?.bsaNumbers?.[0] || null;

  useEffect(() => {
    setMode('light');
  }, [mode, setMode]);

  React.useEffect(() => {
    let active = true;
    const docRef = doc(store, 'games', gameId);
    const unsubscribe = onSnapshot(docRef, async (docSnap) => {
      // The venue lookup makes this handler async, so two quick writes (a
      // photo then a score) can resolve out of order. Only the newest
      // snapshot may set state.
      const seq = ++snapshotSeq.current;

      if (docSnap.exists()) {
        const gameData = { id: docSnap.id, ...docSnap.data() };
        // If venue is an ID, fetch venue details
        if (gameData.venue && typeof gameData.venue === 'string') {
          try {
            const venueDoc = await getDoc(doc(store, 'venues', gameData.venue));
            if (venueDoc.exists()) {
              gameData.venue = { id: venueDoc.id, ...venueDoc.data() };
            }
          } catch (e) {
            // Optionally handle error
          }
        }
        if (!active || seq !== snapshotSeq.current) return;
        setFSGame(gameData);
      } else {
        console.log('No game found!');
      }
    });

    return () => {
      active = false;
      snapshotSeq.current += 1;
      unsubscribe();
    };
  }, [gameId]);

  // Standalone quick games: render the public status card so NOT_STARTED games
  // get a Start button and totals/final-score games get the correct entry UI.
  const standalone = !fsGame.event;
  const totalsMethod =
    fsGame.scoringMethod === 'inputfinalscore' || fsGame.scoringMethod === 'inputtotals';

  const renderPublicBasicCard =
    standalone && (fsGame.status === GameStatus.NOT_STARTED || totalsMethod);

  return fsGame ? (
    <Box>
      <Box
        display={'flex'}
        flexDirection={'column'}
        alignItems={'center'}
        justifyContent={'center'}
        mb={2}>
        <Typography variant="h5">{fsGame.name ?? 'Private Game'}</Typography>
        {fsGame.venue && typeof fsGame.venue === 'object' && (
          <Typography variant="subtitle1" mb={1}>
            {fsGame.venue.name ?? fsGame.venue.id}
          </Typography>
        )}
        {bsaNumber && (
          <Button
            variant="outlined"
            color="info"
            size="small"
            component={Link}
            to={`/public/quickgames/${encodeURIComponent(bsaNumber)}`}
            sx={{ mt: 1 }}>
            Back To Quick Games
          </Button>
        )}
      </Box>

      {renderPublicBasicCard ? (
        <GameByStatusCardPublicBasic game={fsGame} actions={false} bsaNumber={bsaNumber} />
      ) : (
        <>
          <ScoringDisplay game={fsGame} isPublic={true} />
          {fsGame.sets?.length && <Scoreboard game={fsGame} />}
        </>
      )}

      <Box flexDirection={'column'} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Typography variant="caption" textAlign={'center'}>
          Game ID: {fsGame.id}
        </Typography>
        {fsGame.scorecardPhoto && <ViewScorecardPhoto game={fsGame} />}
      </Box>
    </Box>
  ) : (
    <CircularProgress />
  );
};

export default PublicScoring;
